import { useQuery } from '@tanstack/react-query';
import { AlexaVisibilityResult } from '@blisspointmedia/bpm-types/dist/AlexaVisibility';
import { AlexaVisibilityLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';

export interface FetchAlexaVisibilityResultsParams {
  company: string;
  brand: string;
  dateFrom?: string;
  dateTo?: string;
  run?: number;
}

/** One call to the results endpoint; the server returns at most the newest 1,000 matches. */
export const fetchAlexaVisibilityResults = async ({
  company,
  brand,
  dateFrom,
  dateTo,
  run
}: FetchAlexaVisibilityResultsParams): Promise<AlexaVisibilityResult[]> => {
  const params: Record<string, string | number> = { company, brand };
  if (dateFrom) {
    params.dateFrom = dateFrom;
  }
  if (dateTo) {
    params.dateTo = dateTo;
  }
  if (run !== undefined) {
    params.run = run;
  }

  const response = await AlexaVisibilityLambdaFetch('/results', {
    method: 'GET',
    params
  });
  return awaitJSON<AlexaVisibilityResult[]>(response);
};

// Results per request when walking a brand's history; the server allows at most 500.
const RESULTS_PAGE_SIZE = 500;

interface ResultsPage {
  results: AlexaVisibilityResult[];
  nextCursor?: string;
}

/**
 * Every result matching the filters, however many there are: requests pages (newest first) until
 * the server reports no more. Unlike `fetchAlexaVisibilityResults`, there is no 1,000-result cap.
 */
export const fetchAllAlexaVisibilityResults = async ({
  company,
  brand,
  dateFrom,
  dateTo,
  run
}: FetchAlexaVisibilityResultsParams): Promise<AlexaVisibilityResult[]> => {
  const all: AlexaVisibilityResult[] = [];
  let cursor: string | undefined;
  do {
    const params: Record<string, string | number> = {
      company,
      brand,
      limit: RESULTS_PAGE_SIZE
    };
    if (dateFrom) {
      params.dateFrom = dateFrom;
    }
    if (dateTo) {
      params.dateTo = dateTo;
    }
    if (run !== undefined) {
      params.run = run;
    }
    if (cursor) {
      params.cursor = cursor;
    }

    const response = await AlexaVisibilityLambdaFetch('/results', { method: 'GET', params });

    const page = await awaitJSON<ResultsPage | AlexaVisibilityResult[]>(response);
    // A backend that hasn't deployed paging yet ignores `limit` and returns the plain array.
    if (Array.isArray(page)) {
      return page;
    }
    all.push(...page.results);
    cursor = page.nextCursor;
  } while (cursor);
  return all;
};

interface UseAlexaVisibilityResultsParams extends FetchAlexaVisibilityResultsParams {
  // The current latest run's id + status for this brand. Folded into the
  // queryKey (rather than left to a separate invalidation effect) so that a
  // run transitioning pending -> running -> succeeded/failed automatically
  // refetches this query -- without it, a completed run's rows never appear
  // until an unrelated refetch (refocus, brand switch, reload) happens to occur.
  latestRunId?: string;
  latestRunStatus?: string;
}

// Fetches the result history for a (company, brand) pair -- every run, every query -- so every
// tab can share one query. The page narrows it by date client-side and each tab aggregates
// whatever results it is given. Callers must pass `dateFrom` (or `run`) to get more than the latest
// run: the endpoint narrows an unfiltered request to the most recent run. Pages through the whole
// history, so a brand's results are never cut off.
export const useAlexaVisibilityResults = ({
  company,
  brand,
  dateFrom,
  dateTo,
  run,
  latestRunId,
  latestRunStatus
}: UseAlexaVisibilityResultsParams): ReturnType<typeof useQuery<AlexaVisibilityResult[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: [
      'alexaVisibilityResults',
      company,
      brand,
      dateFrom,
      dateTo,
      run,
      latestRunId,
      latestRunStatus
    ],
    queryFn: () => fetchAllAlexaVisibilityResults({ company, brand, dateFrom, dateTo, run }),
    enabled: Boolean(company) && Boolean(brand)
  });
